import { useOrganization } from '@airship-platform/airship-patterns';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { type MangoQuery } from 'rxdb';
import { bufferTime, concatMap, filter, merge, ReplaySubject } from 'rxjs';
import { AppointmentProposalOptionModifier } from '../../../data-access/appointmentProposalOptionModifiers';
import {
  AppointmentProposalOptionProduct,
  useAppointmentProposalOptionProducts,
} from '../../../data-access/appointmentProposalOptionProducts';
import { useAppointmentProposalOptions } from '../../../data-access/appointmentProposalOptions';
import { useAppointments } from '../../../data-access/appointments';
import { useOfflineOptIn } from '../../rxdb/useOfflineOptIn';
import { useOfflineProviderInit } from '../../rxdb/useOfflineProviderInit';
import { useOfflineDbData } from '../../rxdb/utils/OfflineDbProvider';
import RxDBSetupNotificationAlert from '../../rxdb/utils/RxDBSetupNotificationAlert';
import { type CollectionName, type DocOf } from '../../rxdb/utils/types';
import { buildOptionCosts } from './appointmentProposalCosts.utils';
import { useProductMembershipPricing } from './useProductMembershipPricing';

type ProposalItem =
  | AppointmentProposalOptionProduct
  | AppointmentProposalOptionModifier;

const filterItems = <T extends ProposalItem>(items: T[], optionId: string) =>
  items.filter(
    (item) =>
      item.appointmentProposalOptionId === optionId && item.status !== 'DELETED'
  );

const buildKey = <T extends ProposalItem>(item: T) =>
  [
    item.appointmentId,
    item.appointmentProposalId,
    item.appointmentProposalOptionId,
  ].join('|');

const parseKey = (key: string) => {
  const [appointmentId, appointmentProposalId, optionId] = key.split('|');
  return { appointmentId, appointmentProposalId, optionId };
};

interface AppointmentProposalCostsContextProps {
  costsCalculationError: string | null;
  isSyncingCosts: boolean;
  triggerCostsCalculation: (optionId: string) => void;
}

const AppointmentProposalCostsContext =
  createContext<AppointmentProposalCostsContextProps | null>(null);

export const AppointmentProposalCostsProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const data = useOfflineDbData();
  const { currentAppointment } = useAppointments();
  const { organization } = useOrganization();
  const {
    currentOption,
    updateCosts,
    updateAppointmentProposalOptionEnrichment,
  } = useAppointmentProposalOptions();
  const { upsertProducts, clearProductsMutating } =
    useAppointmentProposalOptionProducts();
  const { updateMembershipForProducts } = useProductMembershipPricing();

  const { isOptedIn } = useOfflineOptIn();
  const [isSyncingCosts, setIsSyncingCosts] = useState(false);
  const [costsCalculationError, setCostsCalculationError] = useState<
    string | null
  >(null);
  const subjectRef = useRef(
    new ReplaySubject<Pick<ProposalItem, 'appointmentProposalOptionId'>>(1)
  );

  const changeSetRef = useRef<Map<string, string>>(new Map());
  const membershipPricingEnabled =
    organization?.costSettings?.membershipPricingEnabled;

  const init = useCallback(async () => {
    if (currentOption?.appointmentProposalOptionId) {
      subjectRef.current.next({
        appointmentProposalOptionId: currentOption.appointmentProposalOptionId,
      });
    }
  }, [currentOption?.appointmentProposalOptionId]);

  const { initError } = useOfflineProviderInit(init, init);

  const getProposalData = useCallback(
    async <K extends CollectionName>(optionIds: string[], collection: K) =>
      data.find(collection, {
        selector: { appointmentProposalOptionId: { $in: optionIds } },
      } as MangoQuery<DocOf<K>>),
    [data]
  );

  const awaitPendingSync = useCallback(
    async (optionIds: string[]) => {
      const MAX_TRIES = 20;

      const check = async () =>
        Promise.all([
          getProposalData(optionIds, 'appointment_proposal_option_products'),
          getProposalData(optionIds, 'appointment_proposal_option_modifiers'),
          getProposalData(optionIds, 'appointment_proposal_options'),
        ]);

      let latest = await check();
      const hasPendingChanges = () =>
        latest.flat().some((item) => !item.syncedAt);

      let tries = 0;
      while (hasPendingChanges() && tries++ < MAX_TRIES) {
        await new Promise((resolve) => setTimeout(resolve, 250));
        latest = await check();
      }

      const [products, modifiers] = latest;
      return { products, modifiers };
    },
    [getProposalData]
  );

  const buildChangeSet = useCallback(
    (
      products: AppointmentProposalOptionProduct[],
      modifiers: AppointmentProposalOptionModifier[]
    ) =>
      [
        ...products.reduce<string[]>(
          (prev, p) =>
            prev.concat([
              p.appointmentProposalOptionProductId,
              p.defaultPriceType,
              p.offlineSyncErrors ? 'ERROR' : 'OK',
              (p.price * p.quantity).toString(),
            ]),
          []
        ),
        ...modifiers.reduce<string[]>(
          (prev, p) =>
            prev.concat([
              p.appointmentProposalOptionModifierId,
              p.offlineSyncErrors ? 'ERROR' : 'OK',
              p.amount.toString(),
            ]),
          []
        ),
      ]
        .sort()
        .join(','),
    []
  );

  const updateOptionCosts = useCallback(
    (
      optionId: string,
      products: AppointmentProposalOptionProduct[],
      modifiers: AppointmentProposalOptionModifier[]
    ) => {
      const settings = organization?.costSettings;
      if (!settings) {
        return;
      }

      const costs = buildOptionCosts(products, modifiers, {
        taxRate: currentAppointment?.location.taxRate || 0,
        linkedItemsChargeable: settings.linkedItemsChargeable,
        membershipPricingEnabled: settings.membershipPricingEnabled,
      });

      updateCosts(optionId, costs);
    },
    [
      currentAppointment?.location.taxRate,
      organization?.costSettings,
      updateCosts,
    ]
  );

  const processOptionCosts = useCallback(
    async (
      products: AppointmentProposalOptionProduct[],
      modifiers: AppointmentProposalOptionModifier[]
    ) => {
      const changes = Array.from(
        new Set([...products, ...modifiers].map(buildKey))
      ).flatMap((optionKey) => {
        const { appointmentId, appointmentProposalId, optionId } =
          parseKey(optionKey);
        const optionProducts = filterItems(products, optionId);
        const optionModifiers = filterItems(modifiers, optionId);
        const changeSet = buildChangeSet(optionProducts, optionModifiers);
        const previousChangeSet = changeSetRef.current.get(optionId);
        if (changeSet === previousChangeSet) {
          return [];
        }
        return [
          {
            appointmentId,
            appointmentProposalId,
            optionId,
            optionProducts,
            optionModifiers,
            changeSet,
          },
        ];
      });

      await Promise.all(
        changes.map(
          async ({
            appointmentId,
            appointmentProposalId,
            optionId,
            optionProducts,
            optionModifiers,
            changeSet,
          }) => {
            if (isOptedIn) {
              changeSetRef.current.set(optionId, changeSet);
              return updateOptionCosts(
                optionId,
                optionProducts,
                optionModifiers
              );
            }

            const { products: latestProducts } = await awaitPendingSync([
              optionId,
            ]);

            const res = await updateAppointmentProposalOptionEnrichment(
              appointmentId,
              appointmentProposalId,
              optionId
            );

            changeSetRef.current.set(optionId, changeSet);

            if (res?.products.length) {
              const productsMap = new Map(
                latestProducts.map((p) => [
                  p.appointmentProposalOptionProductId,
                  p.offlineSyncErrors,
                ])
              );
              upsertProducts(
                res.products.map((p) => ({
                  ...p,
                  offlineSyncErrors:
                    productsMap.get(p.appointmentProposalOptionProductId) ||
                    p.offlineSyncErrors,
                }))
              );
            }
          }
        )
      );
    },
    [
      awaitPendingSync,
      buildChangeSet,
      isOptedIn,
      updateAppointmentProposalOptionEnrichment,
      updateOptionCosts,
      upsertProducts,
    ]
  );

  useEffect(() => {
    const sub = subjectRef.current
      .pipe(
        bufferTime(250),
        filter((events) => events.length > 0),
        concatMap(async (items) => {
          setCostsCalculationError(null);
          setIsSyncingCosts(true);

          const optionIds = Array.from(
            new Set(items.map((d) => d.appointmentProposalOptionId))
          );

          try {
            const [productsData, modifiers] = await Promise.all([
              getProposalData(
                optionIds,
                'appointment_proposal_option_products'
              ),
              getProposalData(
                optionIds,
                'appointment_proposal_option_modifiers'
              ),
            ]);
            if (isOptedIn) {
              const products = membershipPricingEnabled
                ? await updateMembershipForProducts(productsData)
                : productsData;
              await processOptionCosts(products, modifiers);
              upsertProducts(products);
            } else {
              await processOptionCosts(productsData, modifiers);
            }
          } catch (error) {
            setCostsCalculationError(
              error instanceof Error ? error.message : `${error}`
            );
          } finally {
            setIsSyncingCosts(false);
            clearProductsMutating({});
          }
        })
      )
      .subscribe();

    return () => sub.unsubscribe();
  }, [
    getProposalData,
    isOptedIn,
    processOptionCosts,
    updateMembershipForProducts,
    upsertProducts,
    clearProductsMutating,
    membershipPricingEnabled,
  ]);

  useEffect(() => {
    const sub = merge(
      data.observeChanges('appointment_proposal_option_products'),
      data.observeChanges('appointment_proposal_option_modifiers')
    ).subscribe(
      (doc) =>
        currentOption?.appointmentProposalId === doc.appointmentProposalId &&
        subjectRef.current.next(doc)
    );

    return () => sub.unsubscribe();
  }, [data, currentOption?.appointmentProposalId]);

  const triggerCostsCalculation: AppointmentProposalCostsContextProps['triggerCostsCalculation'] =
    useCallback(
      (appointmentProposalOptionId) =>
        subjectRef.current.next({ appointmentProposalOptionId }),
      []
    );

  if (initError) {
    return (
      <RxDBSetupNotificationAlert
        error={initError}
        entityName="proposal costs"
      />
    );
  }

  return (
    <AppointmentProposalCostsContext.Provider
      value={{
        costsCalculationError,
        isSyncingCosts,
        triggerCostsCalculation,
      }}
    >
      {children}
    </AppointmentProposalCostsContext.Provider>
  );
};

export const useAppointmentProposalCosts = () => {
  const context = useContext(AppointmentProposalCostsContext);
  if (!context) {
    throw new Error(
      'useAppointmentProposalCosts must be used within an AppointmentProposalCostsProvider'
    );
  }
  return context;
};
